import { useCallback } from 'react';

import { EmergencyNotificationSMSConfiguration } from '@dreryk/egabinet-emergency-notification-frontend-api';

import { Heading, Link, StyledFlex, Text, Textarea } from '@dreryk/react-components';
import { FormContextValues } from '@dreryk/react-common';

import { translationKeys } from '../../../translations';

import {
   StyledSmsPreview,
   StyledSmsPreviewSvg,
   StyledTwoColumnGrid,
} from './EmergencyNotificationSmsConfigurationMessageCustomizablePartsField.styled';
import { EmergencyNotificationSmsConfigurationMessageCustomizablePartsFieldFormBeanPart } from './EmergencyNotificationSmsConfigurationMessageCustomizablePartsField.types';
import { getFullMessage } from './EmergencyNotificationSmsConfigurationMessageCustomizablePartsField.utils';

const MESSAGE_CUSTOMIZABLE_PART_MAX_LENGTH = 300;

type EmergencyNotificationSmsConfigurationMessageCustomizablePartsFieldProps = Pick<
   FormContextValues<EmergencyNotificationSmsConfigurationMessageCustomizablePartsFieldFormBeanPart>,
   'register' | 'watch' | 'setValue'
> & {
   readonly emergencyNotificationSmsConfiguration: EmergencyNotificationSMSConfiguration;
};

export const EmergencyNotificationSmsConfigurationMessageCustomizablePartsField = (
   props: EmergencyNotificationSmsConfigurationMessageCustomizablePartsFieldProps
): JSX.Element => {
   const messageCustomizablePart = props.watch('messageCustomizablePart');
   const { defaultMessage } = props.emergencyNotificationSmsConfiguration;

   const setDefaultValue = useCallback(() => {
      props.setValue('messageCustomizablePart', defaultMessage, { shouldValidate: true });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.setValue, defaultMessage]);

   return (
      <StyledTwoColumnGrid>
         <StyledFlex direction={'column'}>
            <Heading testId={'messageCustomizablePart'} type={'h2'} content={translationKeys.smsText} margin={'8px'} />
            <Textarea
               testId={'messageCustomizablePart'}
               controller={props.register('messageCustomizablePart', {
                  required: true,
               })}
               defaultValue={props.emergencyNotificationSmsConfiguration.messageCustomizablePart}
               rows={6}
               maxRows={8}
               maxLength={MESSAGE_CUSTOMIZABLE_PART_MAX_LENGTH}
               footerContent={
                  messageCustomizablePart === defaultMessage ? null : (
                     <Link
                        testId={'resetMessageCustomizablePart'}
                        onClick={setDefaultValue}
                        label={translationKeys.resetMessageCustomizablePart}
                     />
                  )
               }
            />
         </StyledFlex>
         <StyledFlex direction={'column'}>
            <Heading testId={'messagePreview'} type={'h2'} margin={'8px'} content={translationKeys.smsPreview} />
            <StyledSmsPreview>
               <Text testId={'messageCustomizablePart'}>
                  {getFullMessage(props.emergencyNotificationSmsConfiguration, messageCustomizablePart)}
               </Text>
               <StyledSmsPreviewSvg
                  width={'30'}
                  height={'30'}
                  viewBox={'0 0 30 30'}
                  xmlns={'http://www.w3.org/2000/svg'}
               >
                  <path
                     d={
                        'M29.3169 0.402344V28.8467C24.5572 28.8467 20.1931 27.1542 16.7925 24.3389C13.4001 26.5989 7.96139 28.7487 1.38916 27.5537C3.19942 26.7778 10.1809 22.1234 9.92236 12.8145C9.95309 12.8567 9.98772 12.8961 10.021 12.9365C9.78742 11.725 9.66357 10.4741 9.66357 9.19434V0.402344H29.3169Z'
                     }
                  />
                  <path
                     d={
                        'M29.3169 0.402344H29.7198V-0.000581771H29.3169V0.402344ZM29.3169 28.8467V29.2496H29.7198V28.8467H29.3169ZM16.7925 24.3389L17.0494 24.0285L16.8185 23.8373L16.5691 24.0035L16.7925 24.3389ZM1.38916 27.5537L1.23043 27.1834L0.000112772 27.7107L1.31708 27.9501L1.38916 27.5537ZM9.92236 12.8145L10.2482 12.5775L9.48349 11.526L9.51959 12.8256L9.92236 12.8145ZM10.021 12.9365L9.71006 13.1928L10.4166 12.8602L10.021 12.9365ZM9.66357 9.19434H9.26065V9.19434H9.66357ZM9.66357 0.402344V-0.000581771H9.26065V0.402344H9.66357ZM29.3169 0.402344H28.914V28.8467H29.3169H29.7198V0.402344H29.3169ZM29.3169 28.8467V28.4438C24.6545 28.4438 20.3805 26.7863 17.0494 24.0285L16.7925 24.3389L16.5355 24.6492C20.0057 27.5222 24.46 29.2496 29.3169 29.2496V28.8467ZM16.7925 24.3389L16.5691 24.0035C13.2391 26.2221 7.90505 28.3289 1.46124 27.1573L1.38916 27.5537L1.31708 27.9501C8.01773 29.1684 13.5612 26.9758 17.0159 24.6742L16.7925 24.3389ZM1.38916 27.5537L1.54789 27.9241C2.51474 27.5097 4.76599 26.111 6.74621 23.6357C8.73435 21.1505 10.4575 17.569 10.3251 12.8033L9.92236 12.8145L9.51959 12.8256C9.64579 17.3689 8.00749 20.7691 6.11695 23.1323C4.21849 25.5054 2.07385 26.8219 1.23043 27.1834L1.38916 27.5537ZM9.92236 12.8145L9.5965 13.0514C9.63679 13.1068 9.68751 13.1654 9.71006 13.1928L10.021 12.9365L10.3319 12.6803C10.2879 12.6269 10.2694 12.6066 10.2482 12.5775L9.92236 12.8145ZM10.021 12.9365L10.4166 12.8602C10.1878 11.6734 10.0665 10.448 10.0665 9.19434H9.66357H9.26065C9.26065 10.5001 9.38702 11.7766 9.62536 13.0128L10.021 12.9365ZM9.66357 9.19434H10.0665V0.402344H9.66357H9.26065V9.19434H9.66357ZM9.66357 0.402344V0.805269H29.3169V0.402344V-0.000581771H9.66357V0.402344Z'
                     }
                  />
               </StyledSmsPreviewSvg>
            </StyledSmsPreview>
         </StyledFlex>
      </StyledTwoColumnGrid>
   );
};
